import {
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {
  Visibility,
  TrendingUp,
  CalendarToday,
  School,
} from '@mui/icons-material';
import { CommonStyles } from '../styles/CommonStyles';
import { useQuery } from '@apollo/client';
import { GET_ALL_FINAL_EXAMS_FOR_USER } from '../graphql/getAllFinalExamsForUser';
import { format } from 'date-fns';
import { GetAllFinalExamsForUserQuery } from '../__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import { tagToColor } from '../types/Tag';

const MyExams = () => {
  const { data, loading } = useQuery<GetAllFinalExamsForUserQuery>(
    GET_ALL_FINAL_EXAMS_FOR_USER,
  );
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  const handleRowClick = (examId: string) => {
    navigate(`/final-exam/${examId}`);
  };

  const calculateScore = (
    exam: NonNullable<
      GetAllFinalExamsForUserQuery['getAllFinalExamsForUser']
    >[0],
  ) => {
    const correctAnswers = exam.userQuestions.filter(
      (uq) => uq.selectedOption?.isAnswer === true,
    ).length;
    return `${correctAnswers}/${exam.userQuestions.length}`;
  };

  const calculatePercentage = (
    exam: NonNullable<
      GetAllFinalExamsForUserQuery['getAllFinalExamsForUser']
    >[0],
  ) => {
    const correctAnswers = exam.userQuestions.filter(
      (uq) => uq.selectedOption?.isAnswer === true,
    ).length;
    return exam.userQuestions.length > 0
      ? Math.round((correctAnswers / exam.userQuestions.length) * 100)
      : 0;
  };

  const completedExams = data?.getAllFinalExamsForUser || [];
  const totalExams = completedExams.length;
  const averageScore =
    completedExams.length > 0
      ? Math.round(
          completedExams.reduce(
            (sum, exam) => sum + calculatePercentage(exam),
            0,
          ) / completedExams.length,
        )
      : 0;
  const recentExams = completedExams.filter((exam) => {
    if (!exam.completedAt) return false;
    const examDate = new Date(exam.completedAt);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return examDate > weekAgo;
  }).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Enhanced Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: 'rgb(57, 73, 142)',
            mb: 1,
            textAlign: 'center',
          }}
        >
          My Exams
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgb(100, 100, 100)',
            textAlign: 'center',
            fontSize: '1.1rem',
          }}
        >
          Track your exam performance and review your results
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <School sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalExams}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Total Exams Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {averageScore}%
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Average Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <CalendarToday sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {recentExams}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Exams This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Enhanced Table */}
      {totalExams > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                  }}
                >
                  Date Completed
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                  }}
                >
                  Tags
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                  }}
                >
                  Score
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completedExams.map((exam) => (
                <TableRow
                  key={exam.id}
                  onClick={() => handleRowClick(exam.id)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(57, 73, 142, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                    },
                  }}
                >
                  <TableCell sx={{ py: 2, px: 3, fontSize: '0.95rem' }}>
                    {exam.completedAt
                      ? format(new Date(exam.completedAt), 'MMM d, yyyy h:mm a')
                      : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {exam.tags?.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          sx={{
                            backgroundColor: tagToColor[tag] || '#BDBDBD',
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            height: 32,
                            '& .MuiChip-label': {
                              px: 2,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {calculateScore(exam)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                        }}
                      >
                        ({calculatePercentage(exam)}%)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2, px: 3, textAlign: 'center' }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(exam.id);
                      }}
                      sx={{
                        color: 'rgb(57, 73, 142)',
                        '&:hover': {
                          backgroundColor: 'rgba(57, 73, 142, 0.1)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Empty State */
        <Card
          sx={{
            textAlign: 'center',
            p: 6,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'rgb(245, 247, 250)',
          }}
        >
          <CardContent>
            <School
              sx={{
                fontSize: 80,
                color: 'rgb(57, 73, 142)',
                mb: 2,
                opacity: 0.7,
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: 'rgb(57, 73, 142)' }}
            >
              No Completed Exams Yet
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgb(100, 100, 100)', mb: 3 }}
            >
              Start taking final exams to see your completed exams here. Your
              performance will be tracked and displayed in this table.
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgb(150, 150, 150)' }}>
              Complete your first exam to get started!
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MyExams;
